import React, { useEffect, useRef } from "react";
import { GraduationCap, ScrollText, Home, X } from "lucide-react";
import WardrobeSvg from "./WardrobeSvg";
import homeSections from "@/content/home-sections.json";

const CARD_ICONS = { school: GraduationCap, university: ScrollText, family: Home };
const CARDS = homeSections.wardrobe.cards.map((card) => ({ ...card, Icon: CARD_ICONS[card.icon] || GraduationCap }));
const STATS = homeSections.wardrobe.stats;

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const els = el.querySelectorAll(".reveal");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add("in");
      });
    }, { threshold: 0.12 });
    els.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);
  return ref;
};

export const Section2 = () => {
  const ref = useReveal();
  return (
    <section ref={ref} data-testid="section-wardrobe" className="relative py-28 overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
      <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(118,76,176,0.10), transparent 60%)" }} />

      <div className="container-geniq relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT: text + cards */}
          <div className="lg:col-span-7">
            <h2 className="reveal text-[34px] sm:text-[42px] lg:text-[48px] font-medium leading-[1.08] text-white mb-10 max-w-[640px]">
              {homeSections.wardrobe.title}
            </h2>

            <div className="reveal space-y-5 text-body text-[15.5px] leading-[1.7] max-w-[600px]">
              {homeSections.wardrobe.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph.before}<span style={{ color: "#B79BE0" }}>{paragraph.accent}</span>{paragraph.after}</p>
              ))}
            </div>

            {/* 3 cards */}
            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              {CARDS.map((c) => {
                const Icon = c.Icon;
                return (
                  <div key={c.id} data-testid={`card-${c.id}`} className="reveal geniq-card geniq-card-spotlight p-6 relative overflow-hidden">
                    <div className="flex items-start justify-between mb-5">
                      <h3 className="text-white text-[17px] font-medium">{c.title}</h3>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center border" style={{ borderColor: `${c.accent}80` }}>
                        <Icon size={18} strokeWidth={1.4} style={{ color: c.accent }} />
                      </div>
                    </div>
                    <p className="text-body text-[13.5px] leading-[1.55]">{c.text}</p>
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-white pulse-dot" style={{ boxShadow: `0 0 14px ${c.accent}` }} />
                  </div>
                );
              })}
            </div>

            <p className="reveal mt-8 text-body text-[15px] max-w-[600px]">
              {homeSections.wardrobe.conclusion.before}<span style={{ color: "#B79BE0" }}>{homeSections.wardrobe.conclusion.accent}</span>{homeSections.wardrobe.conclusion.after}
            </p>
          </div>

          {/* RIGHT: wardrobe illustration */}
          <div className="lg:col-span-5 reveal relative">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-10 right-10 w-[400px] h-[400px] rounded-full"
                   style={{ background: "radial-gradient(circle, rgba(118,76,176,0.12), transparent 60%)" }} />
            </div>
            <WardrobeSvg />
          </div>
        </div>

        {/* Statistics panel */}
        <div data-testid="stats-panel" className="reveal mt-20 geniq-glass rounded-[28px] p-7 sm:p-9 grid lg:grid-cols-4 gap-6 items-center">
          <div className="lg:col-span-1">
            <div className="text-white text-[18px] font-medium mb-3">{homeSections.wardrobe.stats_title}</div>
            <div className="h-px w-16 bg-gradient-to-r from-[#764CB0] to-transparent" />
            <div className="mt-3 w-2 h-2 rounded-full bg-[#B79BE0] pulse-dot" />
          </div>
          {STATS.map((s) => (
            <div key={s.id} data-testid={`stat-${s.id}`} className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-md border border-[#764CB0]/50 flex items-center justify-center shrink-0 bg-black/40">
                <X size={20} strokeWidth={1.4} className="text-[#B79BE0]" />
              </div>
              <p className="text-body text-[13px] leading-[1.55]">
                {s.before}<span style={{ color: "#B79BE0" }}>{s.highlight}</span>{s.after}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Section2;
